import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';

import {
  HANDLING_GROUPS,
  HANDLING_MODES,
  REVALIDATE_MODES,
} from '@savgroup-front-common/constants';
import { useToasts } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import { useRoutedStepsOrchestratorContext } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.context';
import { RoutedStepProps } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.types';
import {
  AddressInfoDto,
  PickupPointAddress,
} from '@savgroup-front-common/types';
import { ClaimService } from '@/api';
import { useGetClaimGroupCarrierQuery } from '@/view/app/hooks/useGetClaimGroupCarrierQuery';
import { useGetClaimGroupConfirmation } from '@/view/app/hooks/useGetClaimGroupConfirmation';
import useGetHomePickup from '@/view/app/hooks/useGetHomePickup';
import useGetMultiCarrierProductByIdQuery from '@/view/app/hooks/useGetMultiCarrierProductByIdQuery';

import { getGroupedCarriers } from '../../../../helpers';
import useGetClaimGroupSummary from '../hooks/useGetClaimGroupSummary';
import { IrshStepValues } from '../IrshPages.types';

import NewClaimGroupDeliveryPageSchema from './NewClaimGroupDeliveryPage.schema';
import { ClaimGroupDeliveryValues } from './NewClaimGroupDeliveryPage.types';

interface Args {
  onNextStep: RoutedStepProps<IrshStepValues>['onNextStep'];
}

const useClaimGroupDeliveryPage = ({ onNextStep }: Args) => {
  const { pushErrors } = useToasts();
  const [openModalRelayPointDeposit, setOpenModalRelayPointDeposit] =
    useState(false);
  const [openModalHomeDeposit, setOpenModalHomeDeposit] = useState(false);

  const { values } = useRoutedStepsOrchestratorContext<IrshStepValues>();
  const handling = values?.handling;

  const { homePickupAddress, mail } = useGetHomePickup();
  const claimGroupId = values?.claimGroupId;

  const { claims } = useGetClaimGroupSummary({
    claimGroupId,
    suspense: true,
  });

  const depositAddress =
    claims && claims?.at(0) && claims?.at(0)?.depositAddress
      ? claims?.at(0)?.depositAddress
      : undefined;

  const ownerAddress = depositAddress || homePickupAddress;

  const { claimGroupConfirmation } = useGetClaimGroupConfirmation({
    claimGroupId,
    suspense: true,
  });

  const { carriers, isLoading: isGetCarrierLoading } =
    useGetClaimGroupCarrierQuery({
      claimGroupId,
      handlingMode: HANDLING_MODES.DELIVERY,
    });

  const { multiCarrierProductSummary } = useGetMultiCarrierProductByIdQuery({
    carrierProductIds: carriers
      ?.map((carrier) => carrier.carrierProductId)
      .filter((carrier) => carrier) as string[],
  });

  const { groupedCarriers } = getGroupedCarriers({
    carriers,
    multiCarrierProductSummary,
    handlingInfo: handling,
    handlingMode: HANDLING_MODES.DELIVERY,
  });

  const shouldShowTransportDeduction =
    handling?.canDeduceTransportFromRefund &&
    carriers?.some((carrier) => carrier.priceWithTax);

  const formContext = useForm<ClaimGroupDeliveryValues>({
    defaultValues: {
      addressSelected:
        (values?.handlingDelivery?.depositAddress ?? ownerAddress)
          ? {
              ...ownerAddress,
              firstName: (ownerAddress as any).firstname,
              lastName: (ownerAddress as any).lastname,
              mail,
            }
          : undefined,
      chooseHandling: values?.handlingDelivery?.chooseHandling,
      pickupPointSelected: values?.handlingDelivery?.pickupPointSelected,
    },
    resolver: yupResolver(NewClaimGroupDeliveryPageSchema),
    mode: REVALIDATE_MODES.ON_CHANGE,
  });

  const { handleSubmit, setValue, watch } = formContext;

  const [hasChangeValue, setChangedValue] = useState(false);

  const changeSelection = () => {
    setValue('chooseHandling', undefined);
    setValue('pickupPointSelected', undefined);
    setChangedValue(false);
    setOpenModalRelayPointDeposit(false);
    setOpenModalHomeDeposit(false);
  };

  const resetAddress = () => {
    setValue('pickupPointSelected', undefined);
    setValue('addressSelected', undefined);
  };

  const {
    mutateAsync: handleDeliveryHandling,
    isLoading: isLoadingDeliveryHandlingSubmit,
  } = useMutation(
    ['setDeliveryHandling'],
    async ({
      claimGroupId,
      payload,
    }: {
      claimGroupId?: string;
      payload: {
        handlingMode: HANDLING_MODES;
        pickupPointId?: string;
        pickupPointAddress?: PickupPointAddress | null;
        carrierCustomerPriceId?: string;
        carrierSellerPriceId?: string;
        address?: AddressInfoDto | null;
      };
    }) => {
      const responseHandling = await ClaimService.setClaimGroupHandling({
        claimGroupId,
        payload,
      });

      if (responseHandling.failure) {
        pushErrors(responseHandling.errors);

        return undefined;
      }

      return responseHandling;
    },
  );

  const chooseHandlingSelected = watch('chooseHandling');
  const pickupPointSelected = watch('pickupPointSelected');
  const isPickupPontHandlingSelected = Boolean(
    chooseHandlingSelected === HANDLING_GROUPS.PICKUP_POINT ||
    chooseHandlingSelected === HANDLING_GROUPS.PICKUP_STORE ||
    chooseHandlingSelected === HANDLING_GROUPS.PICKUP_STORE_DELIVERY,
  );

  const homeSelectedAddressChanged = Boolean(
    hasChangeValue &&
    (chooseHandlingSelected === HANDLING_GROUPS.HOME ||
      chooseHandlingSelected === HANDLING_GROUPS.HOME_INTERVENTION ||
      chooseHandlingSelected === HANDLING_GROUPS.EXTERNAL),
  );

  const handleOpenDeliveryModal = (e: React.MouseEvent<HTMLInputElement>) => {
    resetAddress();

    if (
      e.currentTarget.value === HANDLING_GROUPS.HOME ||
      e.currentTarget.value === HANDLING_GROUPS.HOME_INTERVENTION ||
      e.currentTarget.value === HANDLING_GROUPS.EXTERNAL
    ) {
      setOpenModalHomeDeposit((prev) => !prev);

      return;
    }

    if (
      e.currentTarget.value === HANDLING_GROUPS.PICKUP_POINT ||
      e.currentTarget.value === HANDLING_GROUPS.PICKUP_STORE ||
      e.currentTarget.value === HANDLING_GROUPS.PICKUP_STORE_DELIVERY
    ) {
      setOpenModalRelayPointDeposit((prev) => !prev);

      return;
    }

    return;
  };

  const onSubmit = handleSubmit(
    async ({
      chooseHandling,
      pickupPointSelected,
      addressSelected,
      addressSuggested,
      keepSuggestedAddress,
    }) => {
      const minimumPrice = Math.min(
        ...groupedCarriers[chooseHandling || HANDLING_GROUPS.HOME].map(
          (carrier) => carrier.priceWithTax || 0,
        ),
      );

      const carrier = groupedCarriers[
        chooseHandling || HANDLING_GROUPS.HOME
      ].find((carrier) => (carrier.priceWithTax || 0) === minimumPrice);

      if (!carrier) {
        throw new Error('carrier not found');
      }

      const payload = pickupPointSelected
        ? {
            handlingMode: HANDLING_MODES.DELIVERY,
            pickupPointId: pickupPointSelected.id,
            pickupPointAddress: pickupPointSelected && {
              id: pickupPointSelected.id,
              name: pickupPointSelected.name,
              firstName: pickupPointSelected.name,
              address: pickupPointSelected.adress,
              additionalAddress: pickupPointSelected.adress3,
              postalCode: pickupPointSelected.postalCode,
              city: pickupPointSelected.city,
              countryCode: pickupPointSelected.countryCode,
            },
            carrierCustomerPriceId: pickupPointSelected.carrierCustomerPriceId,
            carrierSellerPriceId: pickupPointSelected.carrierSellerPriceId,
            address: undefined,
          }
        : {
            handlingMode: HANDLING_MODES.DELIVERY,
            pickupPointId: undefined,
            pickupPointAddress: null,
            carrierCustomerPriceId: carrier.carrierCustomerPriceId,
            carrierSellerPriceId: carrier.carrierSellerPriceId,
            address: keepSuggestedAddress ? addressSuggested : addressSelected,
          };

      const responseHandling = await handleDeliveryHandling({
        claimGroupId,
        payload,
      });

      if (!responseHandling) {
        return undefined;
      }

      onNextStep({
        newValues: {
          handlingDelivery: {
            chooseHandling,
            pickupPointSelected,
            depositAddress: keepSuggestedAddress
              ? addressSuggested
              : addressSelected,
          },
        },
      });

      return undefined;
    },
  );

  const isDeliveryHandlingLoading = isGetCarrierLoading;

  return {
    claimGroupConfirmation,
    claimGroupId,
    groupedCarriers,
    shouldShowTransportDeduction,
    formContext,
    setChangedValue,
    changeSelection,
    onSubmit,
    isDeliveryHandlingLoading,
    isLoadingSubmit: isLoadingDeliveryHandlingSubmit,
    chooseHandlingSelected,
    handlingSelected: isPickupPontHandlingSelected && !pickupPointSelected,
    homeSelectedAddressChanged,
    openModalRelayPointDeposit,
    openModalHomeDeposit,
    handleOpenDeliveryModal,
  };
};

export default useClaimGroupDeliveryPage;
